import React, { useState, useRef, useEffect } from 'react';
import {
  Play, Pause, Volume2, VolumeX, SkipBack, SkipForward,
  Download, Music, Upload, Edit3, Save, Info, Mic
} from 'lucide-react';
import MediaUploader from './ads/components/MediaUploader';

interface AudioEditablePreviewProps {
  asset: {
    id: number;
    file_name: string;
    metadata?: any;
    content?: any;
    r2_key?: string;
  };
  apiBaseUrl: string;
  onSave?: (assetId: number, content: string) => Promise<void>;
  isEditable?: boolean;
}

interface AudioMetadata {
  title?: string;
  artist?: string;
  album?: string;
  description?: string;
  transcript?: string;
  duration?: string;
  bitrate?: string;
  format?: string;
  genre?: string;
  audioUrl?: string;
  releaseDate?: string;
  copyright?: string;
  license?: string;
  visibility?: 'public' | 'private' | 'unlisted';
  sampleRate?: string;
  channels?: string;
  encoder?: string;
  additionalAudio?: Array<{
    id: string;
    url: string;
    type: 'audio';
    name: string;
    size?: number;
    duration?: string;
  }>;
}

export default function AudioEditablePreview({
  asset,
  apiBaseUrl,
  onSave,
  isEditable = true
}: AudioEditablePreviewProps) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [activeTab, setActiveTab] = useState<'player' | 'details' | 'uploads'>('player');
  const [activeDetailsTab, setActiveDetailsTab] = useState<'basic' | 'technical' | 'publishing' | 'transcript'>('basic');
  const [isSaving, setIsSaving] = useState(false);

  // Parse metadata
  const parseMetadata = (): AudioMetadata => {
    if (typeof asset.metadata === 'string') {
      try {
        return JSON.parse(asset.metadata);
      } catch {
        return {};
      }
    }
    if (typeof asset.content === 'string') {
      try {
        return JSON.parse(asset.content);
      } catch {
        return {};
      }
    }
    return asset.metadata || asset.content || {};
  };

  const [metadata, setMetadata] = useState<AudioMetadata>(parseMetadata());
  const [blobUrls, setBlobUrls] = useState<Set<string>>(new Set());

  // Main audio URL
  const getAudioUrl = () => {
    if (metadata.audioUrl) return metadata.audioUrl;
    if (asset.r2_key) return `${apiBaseUrl}/api/assets/${asset.id}/download`;
    return `${apiBaseUrl}/assets/${asset.id}/download`;
  };

  const assetUrl = getAudioUrl();

  // Blob URL manager for uploads
  const blobManager = {
    add: (url: string) => setBlobUrls(prev => new Set(prev).add(url)),
    remove: (url: string) => {
      setBlobUrls(prev => {
        const next = new Set(prev);
        next.delete(url);
        return next;
      });
      URL.revokeObjectURL(url);
    },
    cleanup: () => {
      blobUrls.forEach(url => URL.revokeObjectURL(url));
      setBlobUrls(new Set());
    }
  };

  useEffect(() => {
    return () => {
      blobManager.cleanup();
    };
  }, []);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const updateTime = () => setCurrentTime(audio.currentTime);
    const updateDuration = () => setDuration(audio.duration);

    audio.addEventListener('timeupdate', updateTime);
    audio.addEventListener('loadedmetadata', updateDuration);

    return () => {
      audio.removeEventListener('timeupdate', updateTime);
      audio.removeEventListener('loadedmetadata', updateDuration);
    };
  }, []);

  const handleSave = async () => {
    if (!onSave) return;

    setIsSaving(true);
    try {
      await onSave(asset.id, JSON.stringify(metadata));
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to save audio metadata:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const togglePlay = () => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!audioRef.current) return;
    const newTime = parseFloat(e.target.value);
    audioRef.current.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!audioRef.current) return;
    const newVolume = parseFloat(e.target.value);
    audioRef.current.volume = newVolume;
    setVolume(newVolume);
    setIsMuted(newVolume === 0);
  };

  const toggleMute = () => {
    if (!audioRef.current) return;
    if (isMuted) {
      audioRef.current.volume = volume || 1;
      setIsMuted(false);
    } else {
      audioRef.current.volume = 0;
      setIsMuted(true);
    }
  };

  const skip = (seconds: number) => {
    if (!audioRef.current) return;
    audioRef.current.currentTime = Math.max(0, Math.min(duration, currentTime + seconds));
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = assetUrl;
    link.download = asset.file_name;
    link.click();
  };

  // Visual waveform effect
  const WaveformVisualizer = () => {
    const bars = Array(40).fill(0);
    return (
      <div className="flex items-center justify-center space-x-1 h-24">
        {bars.map((_, i) => (
          <div
            key={i}
            className={`w-1 bg-gradient-to-t from-indigo-500 to-purple-500 rounded-full transition-all duration-300 ${
              isPlaying ? 'animate-pulse' : ''
            }`}
            style={{
              height: `${Math.random() * 60 + 20}%`,
              animationDelay: `${i * 50}ms`
            }}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Music className="w-5 h-5 text-indigo-600" />
            <h2 className="text-lg font-semibold">{metadata.title || asset.file_name}</h2>
            {metadata.artist && (
              <span className="text-sm text-gray-500">by {metadata.artist}</span>
            )}
          </div>

          {/* Tabs */}
          <div className="flex space-x-1">
            <button
              onClick={() => setActiveTab('player')}
              className={`px-4 py-2 rounded ${activeTab === 'player' ? 'bg-indigo-100 text-indigo-700' : 'hover:bg-gray-100'}`}
            >
              <Play className="w-4 h-4 inline mr-1" />
              Player
            </button>
            <button
              onClick={() => setActiveTab('details')}
              className={`px-4 py-2 rounded ${activeTab === 'details' ? 'bg-indigo-100 text-indigo-700' : 'hover:bg-gray-100'}`}
            >
              <Info className="w-4 h-4 inline mr-1" />
              Details
            </button>
            {isEditable && (
              <button
                onClick={() => setActiveTab('uploads')}
                className={`px-4 py-2 rounded ${activeTab === 'uploads' ? 'bg-indigo-100 text-indigo-700' : 'hover:bg-gray-100'}`}
              >
                <Upload className="w-4 h-4 inline mr-1" />
                Media
              </button>
            )}
          </div>

          {isEditable && activeTab !== 'player' && (
            <button
              onClick={() => isEditing ? handleSave() : setIsEditing(true)}
              disabled={isSaving}
              className="flex items-center space-x-2 px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:bg-gray-400"
            >
              {isEditing ? (
                <>
                  <Save className="w-4 h-4" />
                  <span>{isSaving ? 'Saving...' : 'Save'}</span>
                </>
              ) : (
                <>
                  <Edit3 className="w-4 h-4" />
                  <span>Edit</span>
                </>
              )}
            </button>
          )}
        </div>
      </div>

      {/* Hidden Audio Element */}
      <audio
        ref={audioRef}
        src={assetUrl}
        onEnded={() => setIsPlaying(false)}
      />

      {/* Content */}
      <div className="flex-1 overflow-auto">
        {activeTab === 'player' && (
          <div className="h-full flex flex-col">
            {/* Gradient Background */}
            <div className="flex-1 bg-gradient-to-br from-indigo-400 via-purple-400 to-pink-400 flex flex-col items-center justify-center p-8">
              {/* Album Art or Visualizer */}
              <div className="bg-white bg-opacity-20 backdrop-blur-lg rounded-2xl p-8 mb-8">
                <div className="w-64 h-64 rounded-lg bg-white bg-opacity-30 flex items-center justify-center">
                  {isPlaying ? (
                    <WaveformVisualizer />
                  ) : (
                    <Music className="w-24 h-24 text-white" />
                  )}
                </div>
              </div>

              {/* Track Info */}
              <div className="text-center text-white mb-8">
                <h3 className="text-2xl font-bold mb-2">{metadata.title || 'Unknown Track'}</h3>
                <p className="text-lg opacity-90">{metadata.artist || 'Unknown Artist'}</p>
                {metadata.album && (
                  <p className="text-sm opacity-75 mt-1">{metadata.album}</p>
                )}
              </div>

              {/* Progress Bar */}
              <div className="w-full max-w-md mb-6">
                <input
                  type="range"
                  min="0"
                  max={duration || 0}
                  value={currentTime}
                  onChange={handleSeek}
                  className="w-full h-2 bg-white bg-opacity-30 rounded-lg appearance-none cursor-pointer"
                  style={{
                    background: `linear-gradient(to right, white 0%, white ${(currentTime / duration) * 100}%, rgba(255,255,255,0.3) ${(currentTime / duration) * 100}%, rgba(255,255,255,0.3) 100%)`
                  }}
                />
                <div className="flex justify-between mt-2 text-sm text-white">
                  <span>{formatTime(currentTime)}</span>
                  <span>{formatTime(duration)}</span>
                </div>
              </div>

              {/* Controls */}
              <div className="flex items-center space-x-4 mb-6">
                <button
                  onClick={() => skip(-15)}
                  className="p-3 bg-white bg-opacity-20 backdrop-blur rounded-full text-white hover:bg-opacity-30"
                  title="Skip back 15s"
                >
                  <SkipBack className="w-6 h-6" />
                </button>
                <button
                  onClick={togglePlay}
                  className="p-4 bg-white rounded-full text-indigo-600 hover:scale-105 transition-transform"
                >
                  {isPlaying ? <Pause className="w-8 h-8" /> : <Play className="w-8 h-8" />}
                </button>
                <button
                  onClick={() => skip(15)}
                  className="p-3 bg-white bg-opacity-20 backdrop-blur rounded-full text-white hover:bg-opacity-30"
                  title="Skip forward 15s"
                >
                  <SkipForward className="w-6 h-6" />
                </button>
              </div>

              {/* Volume and Download */}
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  <button
                    onClick={toggleMute}
                    className="p-2 bg-white bg-opacity-20 backdrop-blur rounded-full text-white hover:bg-opacity-30"
                  >
                    {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
                  </button>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.1"
                    value={isMuted ? 0 : volume}
                    onChange={handleVolumeChange}
                    className="w-24 h-2 bg-white bg-opacity-30 rounded-lg appearance-none cursor-pointer"
                  />
                </div>
                <button
                  onClick={handleDownload}
                  className="p-2 bg-white bg-opacity-20 backdrop-blur rounded-full text-white hover:bg-opacity-30"
                  title="Download"
                >
                  <Download className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'details' && (
          <div className="h-full flex flex-col">
            {/* Subtabs */}
            <div className="bg-gray-50 border-b px-6 py-3">
              <div className="flex space-x-6">
                <button
                  onClick={() => setActiveDetailsTab('basic')}
                  className={`pb-2 px-1 text-sm font-medium border-b-2 transition-colors ${
                    activeDetailsTab === 'basic'
                      ? 'text-indigo-600 border-indigo-600'
                      : 'text-gray-500 border-transparent hover:text-gray-700'
                  }`}
                >
                  Basic Information
                </button>
                <button
                  onClick={() => setActiveDetailsTab('technical')}
                  className={`pb-2 px-1 text-sm font-medium border-b-2 transition-colors ${
                    activeDetailsTab === 'technical'
                      ? 'text-indigo-600 border-indigo-600'
                      : 'text-gray-500 border-transparent hover:text-gray-700'
                  }`}
                >
                  Technical Details
                </button>
                <button
                  onClick={() => setActiveDetailsTab('publishing')}
                  className={`pb-2 px-1 text-sm font-medium border-b-2 transition-colors ${
                    activeDetailsTab === 'publishing'
                      ? 'text-indigo-600 border-indigo-600'
                      : 'text-gray-500 border-transparent hover:text-gray-700'
                  }`}
                >
                  Publishing Settings
                </button>
                <button
                  onClick={() => setActiveDetailsTab('transcript')}
                  className={`pb-2 px-1 text-sm font-medium border-b-2 transition-colors ${
                    activeDetailsTab === 'transcript'
                      ? 'text-indigo-600 border-indigo-600'
                      : 'text-gray-500 border-transparent hover:text-gray-700'
                  }`}
                >
                  Transcript
                </button>
              </div>
            </div>

            {/* Subtab Content */}
            <div className="flex-1 overflow-auto p-6">
              <div className="max-w-4xl mx-auto">
                {activeDetailsTab === 'basic' && (
                  <div className="bg-white rounded-lg shadow-sm border p-6">
                    <h3 className="text-lg font-semibold mb-4 flex items-center">
                      <Mic className="w-5 h-5 mr-2 text-indigo-600" />
                      Basic Information
                    </h3>

                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                          <input
                            type="text"
                            value={metadata.title || ''}
                            onChange={(e) => setMetadata({ ...metadata, title: e.target.value })}
                            disabled={!isEditing}
                            className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-100"
                            placeholder="Track title..."
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Artist</label>
                          <input
                            type="text"
                            value={metadata.artist || ''}
                            onChange={(e) => setMetadata({ ...metadata, artist: e.target.value })}
                            disabled={!isEditing}
                            className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-100"
                            placeholder="Artist name..."
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Album</label>
                          <input
                            type="text"
                            value={metadata.album || ''}
                            onChange={(e) => setMetadata({ ...metadata, album: e.target.value })}
                            disabled={!isEditing}
                            className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-100"
                            placeholder="Album name..."
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Genre</label>
                          <select
                            value={metadata.genre || ''}
                            onChange={(e) => setMetadata({ ...metadata, genre: e.target.value })}
                            disabled={!isEditing}
                            className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-100"
                          >
                            <option value="">Select genre...</option>
                            <option value="podcast">Podcast</option>
                            <option value="music">Music</option>
                            <option value="audiobook">Audiobook</option>
                            <option value="speech">Speech</option>
                            <option value="sound-effect">Sound Effect</option>
                            <option value="other">Other</option>
                          </select>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                        <textarea
                          value={metadata.description || ''}
                          onChange={(e) => setMetadata({ ...metadata, description: e.target.value })}
                          disabled={!isEditing}
                          className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-100"
                          rows={4}
                          placeholder="Audio description..."
                        />
                      </div>
                    </div>
                  </div>
                )}

                {activeDetailsTab === 'technical' && (
                  <div className="bg-white rounded-lg shadow-sm border p-6">
                    <h3 className="text-lg font-semibold mb-4 flex items-center">
                      <Info className="w-5 h-5 mr-2 text-indigo-600" />
                      Technical Details
                    </h3>

                    <div className="space-y-4">
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Duration</label>
                          <input
                            type="text"
                            value={metadata.duration || formatTime(duration)}
                            disabled
                            className="w-full px-3 py-2 border rounded-lg text-sm bg-gray-100"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Bitrate</label>
                          <input
                            type="text"
                            value={metadata.bitrate || ''}
                            onChange={(e) => setMetadata({ ...metadata, bitrate: e.target.value })}
                            disabled={!isEditing}
                            className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-100"
                            placeholder="e.g., 128 kbps"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Format</label>
                          <input
                            type="text"
                            value={metadata.format || ''}
                            onChange={(e) => setMetadata({ ...metadata, format: e.target.value })}
                            disabled={!isEditing}
                            className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-100"
                            placeholder="e.g., MP3, WAV"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Sample Rate</label>
                          <input
                            type="text"
                            value={metadata.sampleRate || ''}
                            onChange={(e) => setMetadata({ ...metadata, sampleRate: e.target.value })}
                            disabled={!isEditing}
                            className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-100"
                            placeholder="e.g., 44.1 kHz"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Channels</label>
                          <select
                            value={metadata.channels || ''}
                            onChange={(e) => setMetadata({ ...metadata, channels: e.target.value })}
                            disabled={!isEditing}
                            className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-100"
                          >
                            <option value="">Select...</option>
                            <option value="mono">Mono</option>
                            <option value="stereo">Stereo</option>
                            <option value="5.1">5.1 Surround</option>
                            <option value="7.1">7.1 Surround</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Encoder</label>
                          <input
                            type="text"
                            value={metadata.encoder || ''}
                            onChange={(e) => setMetadata({ ...metadata, encoder: e.target.value })}
                            disabled={!isEditing}
                            className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-100"
                            placeholder="e.g., LAME"
                          />
                        </div>
                      </div>

                      <div className="pt-4 border-t">
                        <p className="text-sm text-gray-600">
                          <strong>File:</strong> {asset.file_name}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {activeDetailsTab === 'publishing' && (
                  <div className="bg-white rounded-lg shadow-sm border p-6">
                    <h3 className="text-lg font-semibold mb-4">Publishing Settings</h3>

                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Release Date</label>
                          <input
                            type="date"
                            value={metadata.releaseDate || ''}
                            onChange={(e) => setMetadata({ ...metadata, releaseDate: e.target.value })}
                            disabled={!isEditing}
                            className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-100"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Visibility</label>
                          <select
                            value={metadata.visibility || 'public'}
                            onChange={(e) => setMetadata({ ...metadata, visibility: e.target.value as any })}
                            disabled={!isEditing}
                            className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-100"
                          >
                            <option value="public">Public</option>
                            <option value="private">Private</option>
                            <option value="unlisted">Unlisted</option>
                          </select>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Copyright</label>
                        <input
                          type="text"
                          value={metadata.copyright || ''}
                          onChange={(e) => setMetadata({ ...metadata, copyright: e.target.value })}
                          disabled={!isEditing}
                          className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-100"
                          placeholder="© 2024 Company Name"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">License</label>
                        <select
                          value={metadata.license || ''}
                          onChange={(e) => setMetadata({ ...metadata, license: e.target.value })}
                          disabled={!isEditing}
                          className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-100"
                        >
                          <option value="">Select license...</option>
                          <option value="all-rights-reserved">All Rights Reserved</option>
                          <option value="cc-by">Creative Commons BY</option>
                          <option value="cc-by-sa">Creative Commons BY-SA</option>
                          <option value="cc-by-nc">Creative Commons BY-NC</option>
                          <option value="cc-by-nc-sa">Creative Commons BY-NC-SA</option>
                          <option value="cc0">Public Domain (CC0)</option>
                          <option value="custom">Custom License</option>
                        </select>
                      </div>
                    </div>
                  </div>
                )}

                {activeDetailsTab === 'transcript' && (
                  <div className="bg-white rounded-lg shadow-sm border p-6">
                    <h3 className="text-lg font-semibold mb-4">Transcript</h3>

                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Full Transcript
                          <span className="ml-2 text-xs text-gray-500">(For podcasts, audiobooks, or speeches)</span>
                        </label>
                        <textarea
                          value={metadata.transcript || ''}
                          onChange={(e) => setMetadata({ ...metadata, transcript: e.target.value })}
                          disabled={!isEditing}
                          className="w-full px-3 py-2 border rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-100"
                          rows={15}
                          placeholder="Enter or paste the full transcript here..."
                        />
                      </div>

                      <div className="pt-4 border-t">
                        <p className="text-sm text-gray-600">
                          <strong>Note:</strong> Transcripts improve accessibility and SEO for audio content.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'uploads' && isEditable && (
          <div className="p-6 max-w-4xl mx-auto">
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center">
                <Upload className="w-5 h-5 mr-2 text-indigo-600" />
                Additional Audio Files
              </h3>

              <MediaUploader
                creatives={metadata.additionalAudio || []}
                onCreativesChange={(audio) => setMetadata({ ...metadata, additionalAudio: audio })}
                disabled={!isEditing}
                platform="Audio Library"
                acceptedTypes={['audio/*']}
                maxFiles={10}
                blobManager={blobManager}
                contentId={asset.id}
                apiBaseUrl={apiBaseUrl}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}